import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
    AgentExecution,
    AgentExecution_Phase,
    type Agent,
    type AgentCodeContext,
    type SendToAgentExecutionRequest,
    type Prompt,
    type ListPromptsRequest_Filter,
    AgentMode,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { type PlainMessage, toPlainMessage } from "@bufbuild/protobuf";
import { ResourceOperation, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import type { PaginationResponse } from "gitpod-next-api/gitpod/v1/pagination_pb";
import type { GitpodAPI } from "@/api";
import { evictFromCache } from "@/utils/stream-cache";
import { autoPaginate } from "@/queries/pagination";
import { useMemo } from "react";
import {
    agentExecutionEnvironmentIds,
    mostRecentAgentExecution,
    agentExecutionState,
    AgentExecutionState,
} from "@/utils/agent-executions";
import { playAgentDoneNotification } from "@/utils/notifications";
import { userPreferencesQueryKey, type PlainUserPreference } from "@/queries/user-preferences-queries";
import { Mode } from "@/components/ai/prompt-input/SpecModeToggle";

export const Agents = {
    Ona: {
        id: "00000000-0000-0000-0000-000000006100",
    },
    DevelopmentEnvironmentSetup: {
        id: "0196d883-88dd-7447-8d18-3175979fe23b",
    },
    InEnvironmentOna: {
        id: "00000000-0000-0000-0000-000000007100",
    },
    InEnvironmentClaudeCode: {
        id: "00000000-0000-0000-0000-000000007200",
    },
};

export type PlainAgent = PlainMessage<Agent>;
export type PlainAgentExecution = PlainMessage<AgentExecution>;
export type PlainPrompt = PlainMessage<Prompt>;
type PlainAgentExecutionList = {
    agentExecutions: PlainAgentExecution[];
    pagination: PaginationResponse | undefined;
};

const toPlainAgent = (agent: Agent): PlainAgent => {
    return toPlainMessage(agent);
};

export const toPlainAgentExecution = (agentExecution: AgentExecution): PlainAgentExecution => {
    return toPlainMessage(agentExecution);
};

const toPlainPrompt = (prompt: Prompt): PlainPrompt => {
    return toPlainMessage(prompt);
};

export const agentServiceQueryKey = {
    listAgents: () => keyWithPrincipal(["agents", "list"]),
    listAgentExecutions: () => keyWithPrincipal(["agent-execution", "list"]),
    getAgentExecution: (agentExecutionId?: string) => keyWithPrincipal(["agent-execution", "get", agentExecutionId]),
    createAgentExecutionConversationToken: (agentExecutionId?: string) =>
        keyWithPrincipal(["agent-execution", "create-conversation-token", agentExecutionId]),
    listPrompts: (filter?: ListPromptsRequest_Filter) => keyWithPrincipal(["prompts", "list", filter]),
    getPrompt: (promptId?: string) => keyWithPrincipal(["prompts", "get", promptId]),
};

export const handleAgentExecutionEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    if (evt.operation === ResourceOperation.UPDATE || evt.operation === ResourceOperation.UPDATE_STATUS) {
        // NEXT-3361: This is a temporary workaround to ensure we don't fetch agent executions from other users when you're an admin.
        // The proper fix is to have a more fine-grained scope for the agent execution events, but for now we're relying on the heuristics
        // of "if it's not in the cache of the list query, then it's probably not yours".
        const agentExecutions = client.getQueryData<PlainAgentExecutionList>(
            agentServiceQueryKey.listAgentExecutions(),
        );
        const existingIds = new Set(agentExecutions?.agentExecutions.map((execution) => execution.id));
        if (!existingIds.has(evt.resourceId)) {
            return;
        }

        // Get the previous state before updating
        const previousExecution = client.getQueryData<PlainAgentExecution>(
            agentServiceQueryKey.getAgentExecution(evt.resourceId),
        );
        const previousState = previousExecution ? agentExecutionState(previousExecution) : null;

        const agentExecution = await api.agentService.getAgentExecution({ agentExecutionId: evt.resourceId });
        const newExecution = agentExecution.agentExecution
            ? toPlainAgentExecution(agentExecution.agentExecution)
            : undefined;

        setAgentExecutionInCache(client, evt.resourceId, newExecution);

        // Check if the agent execution just completed (not stopped by user)
        if (
            newExecution &&
            previousState &&
            previousState !== AgentExecutionState.READY_FOR_REVIEW &&
            previousState !== AgentExecutionState.FAILED
        ) {
            const newState = agentExecutionState(newExecution);
            const isCompleted =
                newState === AgentExecutionState.READY_FOR_REVIEW || newState === AgentExecutionState.FAILED;
            const wasNotStoppedByUser = newExecution.spec?.desiredPhase !== AgentExecution_Phase.STOPPED;

            if (isCompleted && wasNotStoppedByUser) {
                // Check user preference for notifications
                const preferences = client.getQueryData<{ preferences: PlainUserPreference[] }>(
                    userPreferencesQueryKey.list(),
                );
                const notificationPref = preferences?.preferences.find((p) => p.key === "agent-done-notification");

                if (notificationPref?.value === "sound") {
                    playAgentDoneNotification(newExecution.id);
                }
            }
        }
    } else if (evt.operation === ResourceOperation.CREATE) {
        const agentExecution = await api.agentService.getAgentExecution({ agentExecutionId: evt.resourceId });
        setAgentExecutionInCache(
            client,
            evt.resourceId,
            agentExecution.agentExecution ? toPlainAgentExecution(agentExecution.agentExecution) : undefined,
        );
        await client.invalidateQueries({ queryKey: agentServiceQueryKey.listAgentExecutions() });
    } else if (evt.operation === ResourceOperation.DELETE) {
        setAgentExecutionInCache(client, evt.resourceId, undefined);
    }
};

export const useListAgents = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return useQuery({
        queryKey: agentServiceQueryKey.listAgents(),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const agents = await autoPaginate(
                (params) => api.agentService.listAgents(params),
                {},
                (response) => response.agents,
            );
            return {
                agents: agents.map(toPlainAgent),
            };
        },
        enabled: !!user,
    });
};

// TODO: Add filtering by workflowId / status (status for main page, id for workflow)
export const useListAgentExecutions = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: agentServiceQueryKey.listAgentExecutions(),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const agentExecutions = await autoPaginate(
                (params) => api.agentService.listAgentExecutions(params),
                {
                    filter: {
                        creatorIds: [user.id],
                    },
                },
                (response) => response.agentExecutions,
            );
            return {
                // Provides a stable ordering in the client so we can optimistically add new agent executions which is needed to have them show up in the sidebar instantly.
                agentExecutions: agentExecutions.map(toPlainAgentExecution).sort((a, b) => {
                    return Number(
                        (b.metadata?.createdAt?.seconds ?? BigInt(0)) - (a.metadata?.createdAt?.seconds ?? BigInt(0)),
                    );
                }),
            };
        },
        enabled: !!user,
    });
};

export const useGroupedAgentExecutions = () => {
    const { data: agentExecutions, isPending, isError } = useListAgentExecutions();

    const groupedExecutions = useMemo(() => {
        if (!agentExecutions?.agentExecutions) {
            return {
                environmentScoped: new Map<string, PlainAgentExecution[]>(),
                nonEnvironmentScoped: [],
            };
        }

        const environmentScoped = new Map<string, PlainAgentExecution[]>();
        const nonEnvironmentScoped: PlainAgentExecution[] = [];

        agentExecutions.agentExecutions.forEach((execution) => {
            const environmentIds = agentExecutionEnvironmentIds(execution);
            if (environmentIds.length > 0) {
                for (const environmentId of agentExecutionEnvironmentIds(execution)) {
                    const arr = environmentScoped.get(environmentId) || [];
                    arr.push(execution);
                    environmentScoped.set(environmentId, arr);
                }
            } else {
                nonEnvironmentScoped.push(execution);
            }
        });

        return {
            environmentScoped,
            nonEnvironmentScoped,
        };
    }, [agentExecutions?.agentExecutions]);

    return {
        data: groupedExecutions,
        isPending,
        isError,
    };
};

export const useGetAgentExecution = (agentExecutionId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: agentServiceQueryKey.getAgentExecution(agentExecutionId),
        queryFn: async () => {
            const response = await api.agentService.getAgentExecution({
                agentExecutionId,
            });
            if (!response.agentExecution) {
                throw new Error("Agent execution not found");
            }
            return toPlainAgentExecution(response.agentExecution);
        },
        enabled: !!agentExecutionId,
    });
};

export const useAgentExecutionsByEnvironment = ({
    environmentId,
    agentId,
}: {
    environmentId?: string;
    agentId: string;
}) => {
    const { data: agentExecutions, isPending, isError } = useListAgentExecutions();

    if (!environmentId) {
        return {
            data: undefined,
            isPending: false,
            isError: false,
        };
    }

    const agentExecutionsByEnvironment = agentExecutions?.agentExecutions.filter((execution) => {
        const matchesEnvironment = agentExecutionEnvironmentIds(execution).includes(environmentId);
        const matchesAgent = execution.spec?.agentId === agentId;
        return matchesEnvironment && matchesAgent;
    });

    return {
        data: mostRecentAgentExecution(agentExecutionsByEnvironment ?? []),
        isPending,
        isError,
    };
};

export const useCreateAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            agentId,
            codeContext,
            name,
            mode,
        }: {
            agentId: string;
            codeContext?: AgentCodeContext;
            name?: string;
            mode?: Mode;
        }) => {
            const response = await api.agentService.startAgent({
                agentId: agentId,
                codeContext: codeContext,
                name: name,
                mode: mode === Mode.Spec ? AgentMode.PLANNING : AgentMode.EXECUTION,
            });
            return {
                agentExecutionId: response.agentExecutionId,
            };
        },
        onSuccess: (data, variables) => {
            // Use provided name or fallback to default
            const executionName = variables.name || "New Conversation";

            // Optimistically adding it to the cache so its shows up in the sidebar instantly and the page appears to load faster
            setAgentExecutionInCache(
                queryClient,
                data.agentExecutionId,
                new AgentExecution({
                    id: data.agentExecutionId,
                    spec: {
                        agentId: variables.agentId,
                        codeContext: variables.codeContext,
                    },
                    metadata: {
                        name: executionName,
                    },
                    status: {
                        phase: AgentExecution_Phase.PENDING,
                    },
                }),
            );
        },
        meta: {
            method: "AgentService.StartAgent",
        },
    });
};

export const useImprovePromptForAgent = () => {
    const api = useGitpodAPI();
    return useMutation({
        mutationFn: async ({ agentId, prompt }: { agentId: string; prompt: string }) => {
            return api.agentService.improvePromptForAgent({ agentId, prompt });
        },
        meta: {
            method: "AgentService.ImprovePromptForAgent",
        },
    });
};

export const useDeleteAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agentExecutionId }: { agentExecutionId: string }) => {
            const response = await api.agentService.deleteAgentExecution({
                agentExecutionId,
            });
            return response;
        },
        onSuccess: (_data, variables) => {
            setAgentExecutionInCache(queryClient, variables.agentExecutionId, undefined);
        },
    });
};

export const useStopAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ agentExecutionId }: { agentExecutionId: string }) => {
            const response = await api.agentService.stopAgentExecution({
                agentExecutionId,
            });
            return response;
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: agentServiceQueryKey.getAgentExecution(variables.agentExecutionId),
            });
        },
    });
};

export const useSendToAgentExecution = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: SendToAgentExecutionRequest) => {
            const response = await api.agentService.sendToAgentExecution(request);
            return response;
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: agentServiceQueryKey.getAgentExecution(variables.agentExecutionId),
            });
        },
        meta: {
            method: "AgentService.SendToAgentExecution",
        },
    });
};

export const useCreateAgentExecutionConversationToken = (agentExecutionId?: string) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 45; // 45 minutes
    return useQuery<string, Error>({
        queryKey: agentServiceQueryKey.createAgentExecutionConversationToken(agentExecutionId),
        queryFn: async (): Promise<string> => {
            if (!agentExecutionId) {
                throw new Error("No conversation ID provided");
            }

            const { token } = await api.agentService.createAgentExecutionConversationToken({ agentExecutionId });
            if (!token) {
                throw new Error("Failed to create token");
            }

            return token;
        },
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
        enabled: !!agentExecutionId,
    });
};

function setAgentExecutionInCache(client: QueryClient, agentExecutionId: string, agentExecution?: PlainAgentExecution) {
    client.setQueryData(
        agentServiceQueryKey.getAgentExecution(agentExecutionId),
        agentExecution ? agentExecution : null,
    );
    client.setQueryData(agentServiceQueryKey.listAgentExecutions(), (currentData?: PlainAgentExecutionList) => {
        if (!currentData) {
            return currentData;
        }

        const updatedList = currentData.agentExecutions
            .map((w: PlainAgentExecution) => {
                if (w.id !== agentExecutionId) {
                    return w;
                }
                return agentExecution;
            })
            .filter((w) => !!w);

        // Add to the top of the list if it doesn't already exist
        if (agentExecution && !currentData.agentExecutions.some((w) => w.id === agentExecutionId)) {
            updatedList.unshift(agentExecution);
        }

        return {
            ...currentData,
            agentExecutions: updatedList,
        };
    });

    if (!agentExecution) {
        void evictFromCache("conversation:" + agentExecutionId);
    }
}

export const useListPrompts = (filter?: Partial<ListPromptsRequest_Filter>) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    // Create a complete filter object with defaults to avoid unsafe casting
    const completeFilter: ListPromptsRequest_Filter = {
        isTemplate: false,
        isCommand: false,
        command: "",
        commandPrefix: "",
        ...filter,
    } as ListPromptsRequest_Filter;

    return useQuery({
        queryKey: agentServiceQueryKey.listPrompts(completeFilter),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const prompts = await autoPaginate(
                (params) =>
                    api.agentService.listPrompts({
                        ...params,
                        filter: completeFilter,
                    }),
                {},
                (response) => response.prompts,
            );
            return {
                prompts: prompts.map(toPlainPrompt),
            };
        },
        enabled: !!user,
    });
};

export const useListCommandPrompts = () => {
    return useListPrompts({
        isCommand: true,
    });
};

export const useGetPrompt = (promptId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: agentServiceQueryKey.getPrompt(promptId),
        queryFn: async () => {
            const response = await api.agentService.getPrompt({
                promptId,
            });
            if (!response.prompt) {
                throw new Error("Prompt not found");
            }
            return toPlainPrompt(response.prompt);
        },
        enabled: !!promptId,
    });
};
